import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faEye,
  faFileAlt,
  faFilePdf,
  faFileWord,
  faFilePowerpoint
} from '@fortawesome/free-solid-svg-icons';
import './SimpleDocumentViewer.css';

const SimpleDocumentViewer = ({ 
  isOpen, 
  onClose, 
  documentUrl, 
  documentTitle = "Document", 
  documentType = "document"
}) => {
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Auto-open document and close modal
  useEffect(() => {
    if (isOpen && documentUrl) {
      // Small delay to show the modal briefly, then open document
      const timer = setTimeout(() => {
        window.open(documentUrl, '_blank');
        onClose();
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, documentUrl, onClose]);

  // Get appropriate icon based on document type or URL
  const getDocumentIcon = () => {
    if (documentUrl) {
      if (documentUrl.includes('.pdf') || documentUrl.includes('application/pdf')) {
        return faFilePdf;
      } else if (documentUrl.includes('.doc') || documentUrl.includes('.docx')) {
        return faFileWord;
      } else if (documentUrl.includes('.ppt') || documentUrl.includes('.pptx')) {
        return faFilePowerpoint;
      }
    }
    return faFileAlt;
  };

  const getDocumentTypeText = () => {
    if (documentUrl) {
      if (documentUrl.includes('.pdf')) return 'PDF Document';
      if (documentUrl.includes('.doc') || documentUrl.includes('.docx')) return 'Word Document';
      if (documentUrl.includes('.ppt') || documentUrl.includes('.pptx')) return 'PowerPoint Presentation';
    }
    return documentType;
  };

  if (!isOpen) return null;

  return (
    <div className="simple-document-viewer-overlay">
      <div className="simple-document-viewer-modal">
        {/* Modal Header */}
        <div className="simple-document-viewer-header">
          <div className="document-info">
            <FontAwesomeIcon icon={getDocumentIcon()} className="me-3 text-primary" size="2x" />
            <div>
              <h5 className="mb-1 text-white">{documentTitle}</h5>
              <small className="text-light">{getDocumentTypeText()}</small>
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline-light"
            onClick={onClose}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="simple-document-viewer-body">
          <div className="document-preview-info">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Opening...</span>
            </div>
            <h6 className="text-white mb-3">Opening Document</h6>
            <p className="text-light mb-4">
              Your document is opening in a new tab for the best viewing experience.
            </p>
            
            <div className="d-flex gap-2 justify-content-center">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  window.open(documentUrl, '_blank');
                  onClose();
                }}
              >
                <FontAwesomeIcon icon={faEye} className="me-2" />
                Open Document
              </button>
              <button 
                className="btn btn-outline-light"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
            
            <div className="mt-4">
              <small className="text-light opacity-75">
                💡 Documents open in new tabs to avoid browser restrictions and provide better performance.
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div className="simple-document-viewer-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default SimpleDocumentViewer; 